/**
 * PageSpeedUrlInput Component (Story 4.11, Task 4)
 *
 * Dialog for entering a URL to monitor with Google PageSpeed/Lighthouse.
 * Follows the PropertySelector pattern — separate Dialog controlled by parent state.
 *
 * Features:
 * - URL text input with https:// visual prefix hint
 * - Real-time client-side validation (debounced 500ms)
 * - Loading state with time estimate ("typically takes 10-30 seconds")
 * - Error display for server-side rejections
 * - "Start Analysis" primary button, Cancel secondary
 */

import { useState, useCallback, useRef, useEffect } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Loader2, X, Globe, AlertCircle } from "lucide-react";
import { Button } from "@provenleap/ui";
import type { ToastType } from "../../hooks/useToast";
import { apiClient } from "../../lib/apiClient";

interface PageSpeedUrlInputProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  showToast: (type: ToastType, message: string) => void;
  onIntegrationCreated?: () => void;
}

/** Client-side URL validation — mirrors server-side Zod schema */
function validateUrl(url: string): string | null {
  if (!url.trim()) return "URL is required";
  if (url.length > 2048) return "URL must not exceed 2048 characters";

  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
      return "URL must start with http:// or https://";
    }

    const h = parsed.hostname.toLowerCase();
    if (h === "localhost" || h === "127.0.0.1" || h === "::1") {
      return "Public URLs only — localhost is not supported";
    }
    if (/^(10\.|172\.(1[6-9]|2\d|3[01])\.|192\.168\.|169\.254\.)/.test(h)) {
      return "Public URLs only — private IPs are not supported";
    }
    if (h.endsWith(".local") || h.endsWith(".internal")) {
      return "Public URLs only — internal hostnames are not supported";
    }
    if (!h.includes(".")) {
      return "URL must include a domain with a TLD (e.g., example.com)";
    }
    if (parsed.port && !["80", "443", ""].includes(parsed.port)) {
      return "Non-standard ports are not supported";
    }

    return null; // Valid
  } catch {
    return "Please enter a valid URL starting with http:// or https://";
  }
}

export function PageSpeedUrlInput({
  open,
  onOpenChange,
  clientId,
  showToast,
  onIntegrationCreated,
}: PageSpeedUrlInputProps) {
  const [url, setUrl] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [hostnamePreview, setHostnamePreview] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when dialog opens; clean up debounce timer on close/unmount
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setUrl("");
      setValidationError(null);
      setServerError(null);
      setLoading(false);
      setHostnamePreview(null);
    }
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [open]);

  // Debounced client-side validation
  const handleUrlChange = useCallback((value: string) => {
    setUrl(value);
    setServerError(null);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const error = validateUrl(value);
      setValidationError(error);

      if (!error && value.trim()) {
        try {
          const parsed = new URL(value);
          setHostnamePreview(parsed.hostname.toLowerCase());
        } catch {
          setHostnamePreview(null);
        }
      } else {
        setHostnamePreview(null);
      }
    }, 500);
  }, []);

  const handleSubmit = useCallback(async () => {
    const error = validateUrl(url);
    if (error) {
      setValidationError(error);
      return;
    }

    setLoading(true);
    setServerError(null);

    try {
      const res = await apiClient(`/api/integrations/${clientId}/pagespeed`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({ detail: "An error occurred" }));
        const detail = (body as { detail?: string }).detail || "Failed to create integration";
        setServerError(detail);
        setLoading(false);
        return;
      }

      showToast("success", "PageSpeed monitoring started");
      onIntegrationCreated?.();
      onOpenChange(false);
    } catch {
      setServerError("Unable to reach the server. Check your internet connection.");
    } finally {
      setLoading(false);
    }
  }, [url, clientId, showToast, onIntegrationCreated, onOpenChange]);

  const isValid = !validationError && url.trim().length > 0;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/35" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border-[0.5px] border-input bg-background p-6 shadow-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                Monitor Site Performance
              </DialogPrimitive.Title>
            </div>
            <DialogPrimitive.Close
              className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>

          <DialogPrimitive.Description className="text-sm text-muted-foreground mb-4">
            Enter the URL of the website you want to monitor. Performance scores
            will be tracked for both mobile and desktop.
          </DialogPrimitive.Description>

          {/* URL Input */}
          <div className="space-y-3">
            <div>
              <label
                htmlFor="pagespeed-url"
                className="text-sm font-medium mb-1.5 block"
              >
                Website URL
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground select-none">
                  https://
                </span>
                <input
                  ref={inputRef}
                  id="pagespeed-url"
                  type="url"
                  value={url}
                  onChange={(e) => handleUrlChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && isValid && !loading) {
                      handleSubmit();
                    }
                  }}
                  placeholder="example.com"
                  disabled={loading}
                  className="w-full rounded-lg border border-input bg-background py-2 pl-16 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50"
                  aria-describedby="url-validation"
                />
              </div>

              {/* Validation feedback */}
              <div id="url-validation" className="mt-1.5 min-h-[20px]" aria-live="polite">
                {validationError && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="h-3 w-3 shrink-0" />
                    {validationError}
                  </p>
                )}
                {!validationError && hostnamePreview && (
                  <p className="text-xs text-muted-foreground">
                    Will monitor: {hostnamePreview}
                  </p>
                )}
              </div>
            </div>

            {/* Server error */}
            {serverError && (
              <div className="rounded-md border border-destructive/20 bg-destructive/5 p-3">
                <p className="text-sm text-destructive">{serverError}</p>
              </div>
            )}

            {/* Loading state */}
            {loading && (
              <div className="flex items-center gap-3 rounded-md border border-primary/20 bg-primary/5 p-3">
                <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
                <div>
                  <p className="text-sm font-medium">
                    Analyzing — this typically takes 10-30 seconds
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Testing both mobile and desktop performance...
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 mt-5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={!isValid || loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  Analyzing...
                </>
              ) : (
                "Start Analysis"
              )}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
